import React, { createContext, useContext, useEffect, useState } from 'react';
import type { WebSQLDatabase } from 'expo-sqlite';
import { openDatabase } from './openDatabase';
import { drizzle } from 'drizzle-orm/sqlite-proxy';
import { MIGRATION_001_STATEMENTS } from './migrations/001_initial_schema';
import {
  profiles,
  projects,
  supplies,
  projectSupplies,
  timeEntries,
  counters,
  counterEvents,
  projectPhotos,
  outbox,
  lastPulledAt,
} from './schema';
import type { AppDatabase } from './outbox';

/**
 * Conexão SQLite real do app (expo-sqlite + Drizzle).
 *
 * Decisão de integração — por que `drizzle-orm/sqlite-proxy` e não
 * `drizzle-orm/expo-sqlite`:
 *
 * O driver dedicado `drizzle-orm/expo-sqlite` (presente em drizzle-orm
 * ~0.45.2) foi escrito para a API *síncrona* mais nova do expo-sqlite
 * (`openDatabaseSync` / `execSync`, tipo `SQLiteDatabase`), introduzida a
 * partir do expo-sqlite ~11.3 (Expo SDK 49+). A versão instalada neste
 * projeto é expo-sqlite ~11.1.1 — o pin oficial do Expo SDK 48 (confirmado
 * via `expo install --fix`), que ainda só expõe a API clássica estilo
 * WebSQL (`openDatabase` + `db.transaction(tx => tx.executeSql(...))`,
 * sempre assíncrona/por callback). Ou seja: o driver `drizzle-orm/expo-sqlite`
 * não roda contra o pacote que de fato está instalado.
 *
 * Bumpar o expo-sqlite para a linha ~14.x (mínimo exigido pelo driver
 * nativo do Drizzle) quebraria o pin do SDK 48 e arriscaria mismatch de ABI
 * nativa num EAS Build de verdade. Em vez disso, usamos `drizzle-orm/sqlite-proxy`: um driver
 * "genérico" do Drizzle que aceita qualquer implementação assíncrona de
 * `(sql, params, method) => Promise<{ rows }>` — é o adaptador oficial
 * recomendado pelo próprio Drizzle para engines de SQLite não suportadas
 * nativamente. Aqui ele embrulha `db.transaction/executeSql` do
 * expo-sqlite clássico.
 *
 * Efeito colateral aceito: o resultado é um `BaseSQLiteDatabase<'async'>`
 * (todo método é awaited), diferente do `BaseSQLiteDatabase<'sync'>` usado
 * nos testes com better-sqlite3. `AppDatabase` (src/db/outbox.ts) foi
 * ampliado para aceitar os dois — todo o código de outbox/sync já usa
 * `await` em toda chamada, então funciona nos dois modos sem alteração.
 */

const DB_NAME = 'crochet.db';

export function execute(
  database: WebSQLDatabase,
  sql: string,
  params: unknown[],
  method: 'run' | 'all' | 'values' | 'get'
): Promise<{ rows: any }> {
  return new Promise((resolve, reject) => {
    let settled = false;

    database.transaction(
      (tx) => {
        tx.executeSql(
          sql,
          params as (number | string)[],
          (_tx, resultSet) => {
            settled = true;
            if (method === 'get') {
              const row = resultSet.rows._array[0];
              // Importante: `undefined`, não `[]`, quando nenhuma linha bate.
              // `drizzle-orm/sqlite-proxy`, para o método 'get', faz
              // `const row = rows; if (!row) return void 0;` direto sobre o
              // que este callback devolve (ver session.cjs#mapGetResult) —
              // um array vazio é truthy em JS, então devolver `[]` faz o
              // Drizzle achar que ACHOU uma linha (e montar um objeto com
              // todos os campos `undefined`) quando na verdade não achou
              // nenhuma. Isso quebrava silenciosamente todo `.get()` sobre
              // uma consulta sem resultado neste driver (ex.: primeiro pull
              // de uma tabela nova em src/sync, ou checar se um registro já
              // existe antes de um merge) — sem teste algum pegando, porque
              // os testes de sync usam o driver síncrono
              // (drizzle-orm/better-sqlite3), não este bridge assíncrono.
              resolve({ rows: row ? Object.values(row) : undefined });
            } else {
              resolve({ rows: resultSet.rows._array.map((row) => Object.values(row)) });
            }
          },
          (_tx, error) => {
            settled = true;
            reject(error);
            // Retornar `true` sinaliza à API WebSQL que o erro não foi
            // tratado e a transação deve fazer rollback (comportamento
            // padrão que já queremos aqui).
            return true;
          }
        );
      },
      (error) => {
        // errorCallback no nível da transação — cobre falhas que não
        // passam pelo errorCallback de executeSql (ex.: erro ao abrir a
        // transação em si).
        if (!settled) {
          settled = true;
          reject(error);
        }
      }
    );
  });
}

/**
 * Roda as statements da migração inicial dentro de uma única transação
 * WebSQL (todas aplicam ou nenhuma aplica). `CREATE TABLE/INDEX IF NOT
 * EXISTS` torna a operação idempotente — pode rodar em todo boot do app.
 */
function runMigrations(database: WebSQLDatabase): Promise<void> {
  return new Promise((resolve, reject) => {
    database.transaction(
      (tx) => {
        for (const statement of MIGRATION_001_STATEMENTS) {
          tx.executeSql(statement);
        }
      },
      (error) => reject(error),
      () => resolve()
    );
  });
}

/**
 * Migração incremental (Etapa 13 — coluna `practiced_price` em `projects`).
 *
 * Decisão de schema, documentada aqui porque é a única coisa deste tipo no
 * projeto até agora: este app NÃO tem um sistema de migrações versionado
 * (nenhuma tabela `schema_migrations`, nenhum runner tipo Drizzle Kit no
 * device) — `runMigrations` (acima) só roda `CREATE TABLE/INDEX IF NOT
 * EXISTS`, que é idempotente mas NÃO altera uma tabela que já existe.
 * Adicionar uma coluna nova em `projects` precisa então de dois caminhos
 * complementares:
 *
 *  1. Bancos NOVOS: a coluna já está na própria `CREATE TABLE projects` de
 *     `001_initial_schema.{ts,sql}` — nasce com o schema certo, esta função
 *     não faz nada de útil para eles (mas roda mesmo assim, sem efeito).
 *  2. Bancos EXISTENTES (alguém já rodou o app antes desta task): a tabela
 *     já existe SEM a coluna. SQLite não tem `ALTER TABLE ... ADD COLUMN IF
 *     NOT EXISTS`, então a idempotência tem que vir de outro lugar.
 *
 * Abordagem escolhida: tentar o `ALTER TABLE` sempre e engolir o erro
 * "duplicate column name" (a coluna já existe — é exatamente o caso 1,
 * acima, ou uma segunda chamada desta função no mesmo banco). Qualquer
 * outro erro é tratado como real e propagado.
 *
 * Por que não `PRAGMA table_info(projects)` antes (a outra opção sugerida
 * no brief desta task): a ponte fake de WebSQL usada em
 * `client.test.ts`/`writes.test.ts` só sabe devolver linhas para statements
 * que casam com `/^\s*select/i` — um `PRAGMA` passaria por ela sempre
 * vazio, fazendo o teste achar a coluna sempre ausente e mascarando
 * justamente o caminho "coluna já existe, não faz nada" que este teste
 * precisa cobrir. O padrão try-ALTER-e-engolir-duplicate-column funciona
 * igual tanto no SQLite real (expo-sqlite) quanto no better-sqlite3 usado
 * nos testes (ambos lançam uma mensagem contendo "duplicate column name"
 * para o mesmo erro), então o mesmo código é exercitado nos dois ambientes
 * — não um substituto que só concorda consigo mesmo.
 *
 * Exportada (não `function` privada como `runMigrations`) para que
 * `client.test.ts` possa testar as duas pontas diretamente: banco criado
 * com o schema ANTIGO (sem a coluna) ganha a coluna ao rodar esta função; e
 * uma segunda chamada no mesmo banco não lança.
 */
export function ensurePracticedPriceColumn(database: WebSQLDatabase): Promise<void> {
  return new Promise((resolve, reject) => {
    database.transaction(
      (tx) => {
        tx.executeSql(
          'ALTER TABLE projects ADD COLUMN practiced_price REAL;',
          [],
          () => {},
          (_tx, error) => {
            const message = String((error as { message?: string } | undefined)?.message ?? '').toLowerCase();
            if (message.includes('duplicate column')) {
              // Coluna já existe: não é um erro de verdade, só o sinal de
              // que esta migração incremental já rodou (ou o banco já
              // nasceu com a coluna). `false` diz ao WebSQL "tratei este
              // erro, não faça rollback da transação".
              return false;
            }
            // Qualquer outro erro é inesperado — deixa propagar e faz
            // rollback (`true`, ou não retornar nada, sinaliza "não
            // tratado" para a API WebSQL).
            return true;
          }
        );
      },
      (error) => reject(error),
      () => resolve()
    );
  });
}

/**
 * Migração incremental (Etapa 15 — colunas `status`/`last_error` em
 * `outbox`, para a quarentena de itens com erro não recuperável de push —
 * ver spec S05 §Estados na UI). Mesmo padrão de `ensurePracticedPriceColumn`
 * (acima, único precedente do projeto para isto): tenta o `ALTER TABLE` e
 * engole "duplicate column name" (banco novo já nasce com as colunas via
 * `001_initial_schema.*`, ou esta função já rodou antes no mesmo banco).
 *
 * Diferença em relação a `ensurePracticedPriceColumn`: aqui são duas
 * colunas, não uma, e uma delas (`status`) precisa de um backfill — uma
 * linha já processada ANTES desta migração (tem `processed_at` preenchido)
 * ganharia `status='pending'` pelo DEFAULT do ALTER, e reapareceria
 * erroneamente na fila (getPending passa a filtrar por
 * `status='pending'`, não mais por `processed_at IS NULL`). O UPDATE de
 * backfill roda sempre, na mesma transação — é barato e idempotente (a
 * segunda vez em diante não acha nenhuma linha para atualizar).
 */
export function ensureOutboxQuarantineColumns(database: WebSQLDatabase): Promise<void> {
  return new Promise((resolve, reject) => {
    database.transaction(
      (tx) => {
        tx.executeSql(
          "ALTER TABLE outbox ADD COLUMN status TEXT NOT NULL DEFAULT 'pending';",
          [],
          () => {},
          (_tx, error) => {
            const message = String((error as { message?: string } | undefined)?.message ?? '').toLowerCase();
            if (message.includes('duplicate column')) return false;
            return true;
          }
        );
        tx.executeSql(
          'ALTER TABLE outbox ADD COLUMN last_error TEXT;',
          [],
          () => {},
          (_tx, error) => {
            const message = String((error as { message?: string } | undefined)?.message ?? '').toLowerCase();
            if (message.includes('duplicate column')) return false;
            return true;
          }
        );
        tx.executeSql("UPDATE outbox SET status = 'processed' WHERE processed_at IS NOT NULL AND status = 'pending';");
      },
      (error) => reject(error),
      () => resolve()
    );
  });
}

// Reexporta o mesmo `AppDatabase` usado por src/db/outbox.ts e
// src/sync/index.ts (união 'sync' | 'async' — ver comentário lá) em vez de
// um tipo próprio: assim o `db` devolvido por `useDb()` encaixa direto em
// `syncService.syncAll(db)` sem casts na UI.
export type { AppDatabase };

let rawDatabase: WebSQLDatabase | null = null;
let dbInstance: AppDatabase | null = null;
let migrationPromise: Promise<void> | null = null;

function getRawDatabase(): WebSQLDatabase {
  if (!rawDatabase) {
    rawDatabase = openDatabase(DB_NAME);
  }
  return rawDatabase;
}

/**
 * Retorna a instância Drizzle do banco local, garantindo que a migração já
 * rodou antes de devolver a conexão pronta para uso.
 */
export async function getDb(): Promise<AppDatabase> {
  const raw = getRawDatabase();

  if (!migrationPromise) {
    // `runMigrations` primeiro (cria tabelas que não existem ainda), depois
    // as migrações incrementais (colunas novas em tabelas que já existiam
    // antes da etapa que as introduziu) — sempre nesta ordem, na mesma
    // promise memoizada, para que qualquer chamador de `getDb()` espere
    // todas as etapas.
    migrationPromise = runMigrations(raw)
      .then(() => ensurePracticedPriceColumn(raw))
      .then(() => ensureOutboxQuarantineColumns(raw));
  }
  await migrationPromise;

  if (!dbInstance) {
    dbInstance = drizzle((sql, params, method) => execute(raw, sql, params, method)) as unknown as AppDatabase;
  }

  return dbInstance;
}

/**
 * Apaga todas as linhas de todas as tabelas locais (negócio + outbox +
 * cursor de sync) — usado após uma exclusão de conta bem-sucedida (item 3
 * do fluxo de exclusão da spec S01): a usuária está saindo do app de vez, e
 * deixar dados de negócio (ou uma outbox tentando sincronizar para uma
 * conta que não existe mais) no SQLite do device seria tanto uma violação
 * de privacidade quanto uma fonte de erros de sync silenciosos caso alguém
 * faça login com outra conta no mesmo device depois.
 *
 * `DELETE FROM` (não recriar o arquivo/tabelas): mais simples e mais
 * seguro que fechar/apagar o arquivo do banco por baixo de uma conexão que
 * o resto do app (DbProvider) ainda segura viva — `CREATE TABLE IF NOT
 * EXISTS` da migração já é idempotente, então não precisamos rodar a
 * migração de novo. Uma única transação: ou todas as tabelas esvaziam, ou
 * nenhuma (evita ficar com metade do banco limpo se algo falhar no meio).
 *
 * `profiles` também entra aqui mesmo não tendo `deleted_at` (perfil nunca é
 * soft-deletado em uso normal — regra do schema): a conta inteira deixou de
 * existir, então a linha de perfil local também precisa sumir.
 */
export async function resetLocalDatabase(db: AppDatabase): Promise<void> {
  await db.transaction(async (tx) => {
    await tx.delete(outbox);
    await tx.delete(lastPulledAt);
    await tx.delete(counterEvents);
    await tx.delete(counters);
    await tx.delete(timeEntries);
    await tx.delete(projectSupplies);
    await tx.delete(projectPhotos);
    await tx.delete(supplies);
    await tx.delete(projects);
    await tx.delete(profiles);
  });
}

// --- React Context: disponibiliza o `db` para a árvore de componentes ---

interface DbContextValue {
  db: AppDatabase | null;
  ready: boolean;
  error: Error | null;
}

const DbContext = createContext<DbContextValue>({ db: null, ready: false, error: null });

export function DbProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<DbContextValue>({ db: null, ready: false, error: null });

  useEffect(() => {
    let cancelled = false;
    getDb()
      .then((db) => {
        if (!cancelled) setState({ db, ready: true, error: null });
      })
      .catch((error) => {
        if (!cancelled) setState({ db: null, ready: false, error: error as Error });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return React.createElement(DbContext.Provider, { value: state }, children);
}

/** Hook para acessar o banco local dentro de componentes React. */
export function useDb(): DbContextValue {
  return useContext(DbContext);
}
